import { useState, useEffect } from "react";

import type { App, AppImages } from "../types";

import { useDebouncedValue } from "./useDebouncedValue";

type PreTransformedApp = {
  images: AppImages;
  id: string;
  canonical_id: string;
  current_implementation_id: string;
  name: string;
  slug: string;
};

type Options = {
  limit?: number;
  searchDebounceDelay?: number;
  excludeUpcoming?: boolean;
  includeIsAppAdmin?: boolean;
  includeIsAppCollaborator?: boolean;
  apiHostname?: string;
  onResultsUpdate?: (searchQuery: string, results: any) => void;
  showAgentsNodes: boolean;
};

const DEFAULT_LIMIT = 20;

const dedupeAndTrimWhitespace = (query: string) =>
  query.replace(/^\s+|\s+$/g, "");

/**
 * Provides the ability to search for Zapier apps (services, integrations)
 * and receive a set of matching results.
 */
export const useAppSearchData = ({
  limit,
  searchDebounceDelay = 300,
  excludeUpcoming,
  includeIsAppAdmin,
  includeIsAppCollaborator,
  apiHostname,
  onResultsUpdate,
  showAgentsNodes,
}: Options) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    searchDebounceDelay,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<App[]>([]);

  useEffect(() => {
    const searchQueryTrimmed = dedupeAndTrimWhitespace(debouncedSearchQuery);
    if (searchQueryTrimmed === "") {
      setData([]);
      return;
    }

    const fetchAppData = async () => {
      const searchParams = new URLSearchParams();
      searchParams.append("limit", (limit || DEFAULT_LIMIT).toString());
      searchParams.append("title_search", searchQueryTrimmed);
      if (excludeUpcoming)
        searchParams.append("exclude_upcoming", excludeUpcoming.toString());
      if (includeIsAppAdmin)
        searchParams.append(
          "including_is_app_admin",
          includeIsAppAdmin.toString(),
        );
      if (includeIsAppCollaborator)
        searchParams.append(
          "including_is_app_collaborator",
          includeIsAppCollaborator.toString(),
        );

      try {
        setLoading(true);
        const response = await fetch(
          `${apiHostname}/api/v4/apps?${searchParams.toString()}`,
        );
        if (!response.ok) {
          throw new Error("Response was not OK");
        }
        const preTransformedAppsResponse = await response.json();

        const edges = preTransformedAppsResponse.results.map(
          (app: PreTransformedApp) => ({
            id: app.id,
            canonicalId: app.canonical_id,
            slug: app.slug,
            name: app.name,
            currentImplementation: { id: app.current_implementation_id },
            images: app.images,
          }),
        );

        const filteredEdges = edges.filter((edge: App) => {
          // Include all edges except "Agents" when showAgentsNodes is false
          // This is equivalent to: showAgentsNodes || edge.name !== "Agents"
          return !(!showAgentsNodes && edge.name === "Agents");
        });

        setData(filteredEdges);
        if (onResultsUpdate) {
          onResultsUpdate(searchQueryTrimmed, filteredEdges);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppData();
  }, [
    debouncedSearchQuery,
    apiHostname,
    limit,
    excludeUpcoming,
    includeIsAppAdmin,
    includeIsAppCollaborator,
    onResultsUpdate,
    showAgentsNodes,
  ]);

  return {
    loading,
    onSearch: setSearchQuery,
    searchQuery,
    apps: data,
    setSearchQuery,
  };
};
